import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, Play, Clock, Users, Star, Check, 
  TrendingUp, Palette, Lightbulb, ArrowRight, X, Loader2,
  BookOpen, Target, Award
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursesPage = () => {
  const { user, token } = useUser();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Pagamento completato! Riceverai le istruzioni via email.');
    }
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Pagamento annullato');
    }
  }, [searchParams]);

  const courses = [
    {
      id: 'marketing',
      title: 'Marketing Digitale',
      subtitle: 'Impara a vendere online',
      price: 680,
      lessons: 20,
      duration: '40+ ore',
      students: '1.2k+',
      rating: 4.9,
      icon: TrendingUp,
      color: 'from-blue-500 to-indigo-600',
      description: 'Padroneggia le strategie di marketing digitale per far crescere il tuo business online. Dalla SEO ai social media, dalle ads alla conversion optimization.',
      fullDescription: `Questo corso completo ti guiderà attraverso tutte le competenze necessarie per diventare un esperto di marketing digitale. 

Imparerai a creare campagne pubblicitarie efficaci su Google e Facebook, a ottimizzare i contenuti per i motori di ricerca, a costruire funnel di vendita che convertono e molto altro.

Il corso è pensato sia per principianti che per chi ha già esperienza e vuole approfondire le proprie conoscenze.`,
      topics: [
        'Fondamenti di Marketing Digitale',
        'Social Media Marketing',
        'Google Ads & Facebook Ads',
        'Email Marketing Avanzato',
        'SEO e Content Marketing',
        'Analytics e Ottimizzazione',
        'Funnel di Vendita',
        'Personal Branding'
      ],
      whatYouLearn: [
        'Creare campagne pubblicitarie professionali',
        'Ottimizzare siti web per i motori di ricerca',
        'Costruire una community sui social media',
        'Analizzare dati e prendere decisioni data-driven',
        'Aumentare le conversioni del tuo business'
      ],
      targetAudience: 'Imprenditori, marketer, freelancer e chiunque voglia far crescere un business online'
    },
    {
      id: 'moda',
      title: 'Fashion Business',
      subtitle: 'Crea il tuo brand di moda',
      price: 480,
      lessons: 15,
      duration: '30+ ore',
      students: '850+',
      rating: 4.8,
      icon: Palette,
      color: 'from-pink-500 to-rose-600',
      description: 'Tutto quello che serve per lanciare e gestire un brand di moda. Dal design alla produzione, dal branding alla vendita.',
      fullDescription: `Un percorso completo per chi sogna di creare il proprio brand di moda. Questo corso ti accompagnerà dalla fase di ideazione fino al lancio sul mercato.

Imparerai a sviluppare una collezione, a trovare fornitori affidabili, a costruire un brand memorabile e a vendere online con successo.

Docenti esperti del settore condivideranno le loro esperienze e ti daranno gli strumenti pratici per realizzare il tuo progetto.`,
      topics: [
        'Trend Analysis & Forecasting',
        'Fashion Design Basics',
        'Sourcing e Produzione',
        'Branding per la Moda',
        'E-commerce Fashion',
        'Visual Merchandising',
        'Fashion Photography',
        'Influencer Marketing'
      ],
      whatYouLearn: [
        'Analizzare i trend di mercato',
        'Creare una collezione coerente',
        'Trovare fornitori e produttori affidabili',
        'Costruire un brand di moda riconoscibile',
        'Vendere online con strategie efficaci'
      ],
      targetAudience: 'Aspiranti stilisti, imprenditori della moda, proprietari di boutique'
    },
    {
      id: 'designer',
      title: 'Design & Creativity',
      subtitle: 'Diventa un designer professionista',
      price: 580,
      lessons: 18,
      duration: '35+ ore',
      students: '950+',
      rating: 4.9,
      icon: Lightbulb,
      color: 'from-amber-500 to-orange-600',
      description: 'Sviluppa le tue competenze creative e diventa un designer completo. Grafica, UI/UX, branding e molto altro.',
      fullDescription: `Un corso intensivo per chi vuole trasformare la creatività in una professione. Imparerai a usare gli strumenti professionali del design e a sviluppare un processo creativo efficace.

Dal graphic design all'UI/UX, dalla creazione di identità visive al motion design: un percorso completo per diventare un designer versatile e richiesto.

Include esercitazioni pratiche, feedback personalizzati e la creazione di un portfolio professionale.`,
      topics: [
        'Principi di Design',
        'Color Theory & Typography',
        'Adobe Creative Suite',
        'UI/UX Design',
        'Brand Identity Design',
        'Motion Graphics',
        'Design Thinking',
        'Portfolio Building'
      ],
      whatYouLearn: [
        'Padroneggiare Adobe Illustrator, Photoshop e Figma',
        'Creare identità visive complete',
        'Progettare interfacce user-friendly',
        'Applicare il design thinking ai progetti',
        'Costruire un portfolio professionale'
      ],
      targetAudience: 'Aspiranti designer, creativi, chi vuole cambiare carriera nel design'
    }
  ];

  const handleEnroll = async (course) => {
    if (!user) {
      toast.error('Accedi per acquistare il corso');
      return;
    }
    
    setLoading(true);
    setSelectedCourse(course);
    
    try {
      const response = await axios.post(`${API}/paypal/create-course-payment`, {
        course_id: course.id,
        course_name: course.title,
        amount: course.price
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('PayPal error:', error);
      toast.error(`Errore. Contattaci a postmaster@qantri.store per iscriverti al corso ${course.title}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="courses-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#064E3B] to-[#0A6B4F]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <GraduationCap className="h-4 w-4" />
              <span className="text-sm uppercase tracking-wider">QANTRI Academy</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Investi nel tuo <span className="italic">futuro</span>
            </h1>
            
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Corsi professionali per chi vuole eccellere nel business, nella moda e nel design.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#D4AF37]" />
                <span>Certificato finale</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#D4AF37]" />
                <span>Accesso illimitato</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#D4AF37]" />
                <span>Supporto dedicato</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              I nostri <span className="italic">corsi</span>
            </h2>
            <p className="text-neutral-500 max-w-xl mx-auto">
              Scopri il corso giusto per te. Clicca su "Scopri di più" per vedere tutti i dettagli.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden border border-neutral-200 hover:shadow-2xl transition-all duration-500 group"
              >
                {/* Course Header */}
                <div className={`bg-gradient-to-br ${course.color} p-8 text-white relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <course.icon className="h-12 w-12 mb-4 relative z-10" />
                  <h3 className="text-2xl font-bold mb-1 relative z-10">{course.title}</h3>
                  <p className="text-white/80 text-sm relative z-10">{course.subtitle}</p>
                </div>
                
                {/* Course Body */}
                <div className="p-8">
                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-6 text-sm text-neutral-500">
                    <div className="flex items-center gap-1">
                      <Play className="h-4 w-4" />
                      <span>{course.lessons} lezioni</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{course.duration}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-6 text-sm">
                    <div className="flex items-center gap-1 text-neutral-500">
                      <Users className="h-4 w-4" />
                      <span>{course.students} studenti</span>
                    </div>
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="h-4 w-4 fill-current" />
                      <span className="font-medium">{course.rating}</span>
                    </div>
                  </div>
                  
                  <p className="text-neutral-600 text-sm mb-6 line-clamp-3">
                    {course.description}
                  </p>
                  
                  {/* Price & CTAs */}
                  <div className="space-y-3 pt-6 border-t border-neutral-100">
                    <div className="text-center">
                      <span className="text-3xl font-bold text-neutral-900">€{course.price}</span>
                      <span className="text-neutral-400 text-sm ml-1">/ corso completo</span>
                    </div>
                    
                    <Button
                      onClick={() => setPreviewCourse(course)}
                      variant="outline"
                      className="w-full rounded-full"
                    >
                      <BookOpen className="mr-2 h-4 w-4" />
                      Scopri di più
                    </Button>
                    
                    {user ? (
                      <Button
                        onClick={() => handleEnroll(course)}
                        disabled={loading && selectedCourse?.id === course.id}
                        className={`w-full bg-gradient-to-r ${course.color} text-white hover:opacity-90 rounded-full`}
                      >
                        {loading && selectedCourse?.id === course.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            Acquista corso
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    ) : (
                      <Link to="/login" className="block">
                        <Button className={`w-full bg-gradient-to-r ${course.color} text-white hover:opacity-90 rounded-full`}>
                          Accedi per acquistare
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Preview Modal */}
      <AnimatePresence>
        {previewCourse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewCourse(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`bg-gradient-to-br ${previewCourse.color} p-8 text-white relative`}>
                <button
                  onClick={() => setPreviewCourse(null)}
                  className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
                <previewCourse.icon className="h-16 w-16 mb-4" />
                <h2 className="text-3xl font-bold mb-2">{previewCourse.title}</h2>
                <p className="text-white/80">{previewCourse.subtitle}</p>
                
                <div className="flex items-center gap-6 mt-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    <span>{previewCourse.lessons} lezioni</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{previewCourse.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>{previewCourse.students} studenti</span>
                  </div>
                  <div className="flex items-center gap-2 text-amber-300">
                    <Star className="h-4 w-4 fill-current" />
                    <span>{previewCourse.rating}</span>
                  </div>
                </div>
              </div>
              
              {/* Modal Body */}
              <div className="p-8 space-y-8">
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-[#064E3B]" />
                    Descrizione del corso
                  </h3>
                  <p className="text-neutral-600 whitespace-pre-line">
                    {previewCourse.fullDescription}
                  </p>
                </div>
                
                {/* What you'll learn */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Target className="h-5 w-5 text-[#064E3B]" />
                    Cosa imparerai
                  </h3>
                  <ul className="space-y-2">
                    {previewCourse.whatYouLearn.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-neutral-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Topics */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-[#064E3B]" />
                    Programma del corso
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {previewCourse.topics.map((topic, i) => (
                      <div key={i} className="flex items-center gap-2 bg-neutral-50 px-4 py-2 rounded-lg">
                        <span className="w-6 h-6 bg-[#064E3B] text-white rounded-full flex items-center justify-center text-xs">
                          {i + 1}
                        </span>
                        <span className="text-sm text-neutral-700">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Target Audience */}
                <div className="bg-[#064E3B]/5 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                    <Award className="h-5 w-5 text-[#064E3B]" />
                    A chi è rivolto
                  </h3>
                  <p className="text-neutral-600">{previewCourse.targetAudience}</p>
                </div>
                
                {/* CTA */}
                <div className="flex items-center justify-between pt-6 border-t border-neutral-200">
                  <div>
                    <span className="text-4xl font-bold text-neutral-900">€{previewCourse.price}</span>
                    <span className="text-neutral-400 ml-2">una tantum</span>
                  </div>
                  {user ? (
                    <Button
                      onClick={() => {
                        setPreviewCourse(null);
                        handleEnroll(previewCourse);
                      }}
                      disabled={loading}
                      className={`bg-gradient-to-r ${previewCourse.color} text-white hover:opacity-90 rounded-full px-8 py-6 text-lg`}
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          Acquista ora
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Link to="/login" onClick={() => setPreviewCourse(null)}>
                      <Button className={`bg-gradient-to-r ${previewCourse.color} text-white hover:opacity-90 rounded-full px-8 py-6 text-lg`}>
                        Accedi per acquistare
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section */}
      <section className="py-20 bg-neutral-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-light text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            Domande <span className="italic">frequenti</span>
          </h2>
          
          <div className="space-y-4">
            {[
              { q: 'Come funziona l\'accesso ai corsi?', a: 'Dopo l\'acquisto, riceverai via email le credenziali per accedere alla piattaforma e iniziare subito il corso.' },
              { q: 'Posso accedere ai corsi da mobile?', a: 'Sì, tutti i corsi sono accessibili da qualsiasi dispositivo: computer, tablet o smartphone.' },
              { q: 'Riceverò un certificato?', a: 'Sì, al completamento di ogni corso riceverai un certificato digitale verificabile.' },
              { q: 'C\'è un limite di tempo per completare i corsi?', a: 'No, una volta acquistato il corso avrai accesso illimitato a tutti i contenuti.' }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6">
                <h3 className="font-medium mb-2">{faq.q}</h3>
                <p className="text-neutral-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Hai domande?
          </h2>
          <p className="text-neutral-500 mb-8 max-w-xl mx-auto">
            Contattaci per maggiori informazioni sui corsi e le modalità di accesso.
          </p>
          <a href="mailto:postmaster@qantri.store">
            <Button className="bg-[#064E3B] hover:bg-[#043927] text-white rounded-full px-10 py-6 text-lg">
              Contattaci
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default CoursesPage;
